import React, { useState, useEffect } from 'react';
import {
  Sparkles,
  TrendingUp,
  ShieldAlert,
  Users,
  Compass,
  DollarSign,
  MessageSquare,
  FileCheck,
  Send,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Car,
  ChevronRight,
  ExternalLink,
  ShieldCheck,
  Info,
  Calendar,
  MapPin,
  Bot,
  Sliders,
  Check,
  X,
  FileText,
  Activity,
  ArrowUpRight,
  Zap
} from 'lucide-react';
import {
  fetchAIDashboardSummary,
  fetchAIBusinessIntelligence,
  fetchAIFraudAlerts,
  actionAIFraudAlert,
  fetchAIDemandForecast,
  simulateSmartMatching,
  fetchAIPricingAdvice,
  fetchAISupportDisputes,
  draftAISupportReply,
  prescreenDocumentWithAI,
  sendAIChatQuestion,
  fetchAIAuditLogs,
  AIBusinessSummary,
  AIBusinessIntelligenceData,
  AIFraudAlert,
  AIDemandForecastData,
  AISmartMatchResult,
  AIPricingAdviceItem,
  AISupportDisputeItem,
  AIDocumentPrescreenResult,
  AIChatMessage,
  AIAuditLogItem,
} from '../../../lib/aiAdminService';
import { PairUpLogo } from '../../common/PairUpLogo';

interface AISmartIntelligenceSuiteProps {
  onNavigateToKYC?: () => void;
  onNavigateToUsers?: () => void;
}

export const AISmartIntelligenceSuite: React.FC<AISmartIntelligenceSuiteProps> = ({
  onNavigateToKYC,
  onNavigateToUsers,
}) => {
  // Active Sub-Tab
  const [subTab, setSubTab] = useState<
    | 'overview'
    | 'business_bi'
    | 'fraud_detection'
    | 'demand_forecast'
    | 'smart_matching'
    | 'pricing_advisor'
    | 'support_assistant'
    | 'doc_prescreen'
    | 'chat_assistant'
    | 'audit_logs'
  >('overview');

  // Loading States
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Data States
  const [summary, setSummary] = useState<AIBusinessSummary | null>(null);
  const [biData, setBiData] = useState<AIBusinessIntelligenceData | null>(null);
  const [fraudAlerts, setFraudAlerts] = useState<AIFraudAlert[]>([]);
  const [demandData, setDemandData] = useState<AIDemandForecastData | null>(null);
  const [pricingItems, setPricingItems] = useState<AIPricingAdviceItem[]>([]);
  const [disputes, setDisputes] = useState<AISupportDisputeItem[]>([]);
  const [auditLogs, setAuditLogs] = useState<AIAuditLogItem[]>([]);

  // Filter States
  const [alertFilter, setAlertFilter] = useState<'ALL' | 'OPEN' | 'HIGH' | 'REVIEWED'>('OPEN');
  const [timeframe, setTimeframe] = useState<'WEEKLY' | 'MONTHLY' | 'YEARLY'>('MONTHLY');

  // Interactive Smart Matching Simulator State
  const [matchingCriteria, setMatchingCriteria] = useState({
    category: 'Tourist Guide & Heritage Walks',
    city: 'Kolkata, WB',
    budgetTier: 'Hourly (₹600 - ₹1200)',
    language: 'English, Bengali',
  });
  const [matchingResults, setMatchingResults] = useState<AISmartMatchResult[]>([]);
  const [isMatchingLoading, setIsMatchingLoading] = useState(false);

  // Interactive Chat State
  const [chatMessages, setChatMessages] = useState<AIChatMessage[]>([
    {
      id: 'msg-init-1',
      sender: 'assistant',
      text: 'Hello Owner! I am your Pair Up Today AI Smart Intelligence Assistant powered by Google Gemini (gemini-3.8-flash). I have real-time access to platform bookings, revenue ledgers, companion verifications, and fraud detection flags.\n\nHow can I assist your business decisions today?',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      dataGrounded: true,
      sources: ['Pairings DB', 'Payments Ledger', 'Users Registry'],
    },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [isChatSending, setIsChatSending] = useState(false);

  // Document Prescreen Simulator State
  const [prescreenInput, setPrescreenInput] = useState({
    companionId: 'comp-seed-02',
    companionName: 'Bikramjit Sarkar',
    documentType: 'DRIVING_LICENCE',
    licenceNumber: 'WB02-2019003811',
    expiryDate: '2034-08-15',
  });
  const [prescreenResult, setPrescreenResult] = useState<AIDocumentPrescreenResult | null>(null);
  const [isPrescreening, setIsPrescreening] = useState(false);

  // Feedback banner
  const [notice, setNotice] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  const showNotice = (msg: string, type: 'success' | 'error' | 'info' = 'success') => {
    setNotice({ message: msg, type });
    setTimeout(() => setNotice(null), 4000);
  };

  // Load Initial Data
  const loadAIData = async () => {
    setRefreshing(true);
    try {
      const [sumRes, biRes, alertsRes, forecastRes, priceRes, dispRes, auditRes] = await Promise.all([
        fetchAIDashboardSummary(),
        fetchAIBusinessIntelligence(timeframe),
        fetchAIFraudAlerts(),
        fetchAIDemandForecast(),
        fetchAIPricingAdvice(),
        fetchAISupportDisputes(),
        fetchAIAuditLogs(),
      ]);

      if (sumRes.success && sumRes.data) setSummary(sumRes.data);
      if (biRes.success && biRes.data) setBiData(biRes.data);
      if (alertsRes.success && alertsRes.alerts) setFraudAlerts(alertsRes.alerts);
      if (forecastRes.success && forecastRes.data) setDemandData(forecastRes.data);
      if (priceRes.success && priceRes.items) setPricingItems(priceRes.items);
      if (dispRes.success && dispRes.disputes) setDisputes(dispRes.disputes);
      if (auditRes.success && auditRes.logs) setAuditLogs(auditRes.logs);
    } catch (e) {
      console.warn('AI Admin data fetch warning:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadAIData();
  }, [timeframe]);

  // Handle Alert Human Action
  const handleAlertAction = async (alertId: string, action: 'REVIEW' | 'DISMISS' | 'FLAG_ACCOUNT') => {
    const res = await actionAIFraudAlert(alertId, action, `Admin manually marked as ${action}`);
    if (res.success && res.alert) {
      setFraudAlerts((prev) => prev.map((a) => (a.id === alertId ? res.alert! : a)));
      showNotice(`Alert ${alertId} processed. Human signoff recorded in audit log.`, 'success');
    } else {
      showNotice(res.error || 'Failed to update alert', 'error');
    }
  };

  // Handle Matching Simulation
  const handleRunMatching = async () => {
    setIsMatchingLoading(true);
    const res = await simulateSmartMatching(matchingCriteria);
    if (res.success && res.matches) {
      setMatchingResults(res.matches);
    } else {
      showNotice(res.error || 'Failed to run matching simulation', 'error');
    }
    setIsMatchingLoading(false);
  };

  // Handle AI Chat
  const handleSendChat = async (e?: React.FormEvent, presetPrompt?: string) => {
    if (e) e.preventDefault();
    const promptToSend = presetPrompt || chatInput.trim();
    if (!promptToSend || isChatSending) return;

    const userMsg: AIChatMessage = {
      id: `usr-${Date.now()}`,
      sender: 'user',
      text: promptToSend,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setChatMessages((prev) => [...prev, userMsg]);
    if (!presetPrompt) setChatInput('');
    setIsChatSending(true);

    try {
      const history = chatMessages.slice(-6).map((m) => ({
        role: m.sender === 'user' ? ('user' as const) : ('assistant' as const),
        text: m.text,
      }));

      const res = await sendAIChatQuestion(promptToSend, history);
      if (res.success && res.reply) {
        const aiMsg: AIChatMessage = {
          id: `ai-${Date.now()}`,
          sender: 'assistant',
          text: res.reply,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          dataGrounded: true,
          sources: res.groundedSources,
        };
        setChatMessages((prev) => [...prev, aiMsg]);
      } else {
        const errorMsg: AIChatMessage = {
          id: `ai-err-${Date.now()}`,
          sender: 'assistant',
          text: 'I could not process that request right now. Please verify backend connectivity.',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        };
        setChatMessages((prev) => [...prev, errorMsg]);
      }
    } catch (err: any) {
      showNotice('Chat error: ' + err.message, 'error');
    } finally {
      setIsChatSending(false);
    }
  };

  // Handle Document Prescreening
  const handleRunPrescreen = async () => {
    setIsPrescreening(true);
    const res = await prescreenDocumentWithAI(prescreenInput);
    if (res.success && res.prescreenResult) {
      setPrescreenResult(res.prescreenResult);
      showNotice('Document pre-screen complete. Ready for Admin KYC review.', 'success');
    } else {
      showNotice(res.error || 'Prescreen failed', 'error');
    }
    setIsPrescreening(false);
  };

  // Filtered alerts
  const filteredAlerts = fraudAlerts.filter((a) => {
    if (alertFilter === 'OPEN') return a.status === 'OPEN';
    if (alertFilter === 'HIGH') return a.riskLevel === 'HIGH';
    if (alertFilter === 'REVIEWED') return a.status === 'REVIEWED' || a.status === 'ACTION_TAKEN';
    return true;
  });

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Toast Notice */}
      {notice && (
        <div
          className={`p-4 rounded-2xl flex items-center justify-between text-xs font-bold border transition-all ${
            notice.type === 'success'
              ? 'bg-emerald-950/90 border-emerald-800 text-emerald-200'
              : notice.type === 'error'
              ? 'bg-rose-950/90 border-rose-800 text-rose-200'
              : 'bg-purple-950/90 border-purple-800 text-purple-200'
          }`}
        >
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            <span>{notice.message}</span>
          </div>
          <button onClick={() => setNotice(null)} className="p-1 hover:text-white cursor-pointer">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ========================================================================= */}
      {/* HEADER: PAIR UP TODAY AI INTELLIGENCE (Owner Dashboard Preview Match)     */}
      {/* ========================================================================= */}
      <div className="relative rounded-3xl bg-gradient-to-r from-slate-900 via-purple-950/70 to-slate-900 border border-purple-900/60 p-6 sm:p-8 overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <PairUpLogo className="w-10 h-10 drop-shadow-md" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-bold tracking-widest text-purple-400 uppercase">
                    PAIR UP TODAY
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-purple-500/20 border border-purple-400/30 text-purple-300 text-[10px] font-mono font-bold flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Gemini 3.8 Flash Active
                  </span>
                </div>
                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-2">
                  AI Smart Intelligence System
                  <Sparkles className="w-6 h-6 text-purple-400" />
                </h1>
              </div>
            </div>
            <p className="text-xs sm:text-sm text-slate-300 max-w-2xl leading-relaxed">
              Private Owner Dashboard · Intelligent business analytics, fraud anomaly alerts, 
              demand predictions, smart companion matching, and automated decision-support assistant.
            </p>
          </div>

          <div className="flex items-center gap-2.5">
            <button
              onClick={loadAIData}
              disabled={refreshing}
              className="px-4 py-2.5 rounded-xl bg-slate-800/90 hover:bg-slate-800 border border-slate-700 text-slate-200 text-xs font-bold flex items-center gap-2 transition-all cursor-pointer hover:border-purple-500"
              title="Refresh AI Telemetry"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin text-purple-400' : ''}`} />
              <span>{refreshing ? 'Syncing...' : 'Sync AI Engine'}</span>
            </button>
            <button
              onClick={() => setSubTab('chat_assistant')}
              className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-xs font-black flex items-center gap-2 shadow-lg shadow-purple-950 transition-all cursor-pointer hover:scale-105"
            >
              <Bot className="w-4 h-4" />
              <span>Ask AI Assistant</span>
            </button>
          </div>
        </div>

        {/* 4 CORE KPI CARDS (Matching Specification Concept Preview) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-6 pt-6 border-t border-slate-800/80">
          {/* 1. Total Bookings */}
          <div className="p-4 rounded-2xl bg-slate-950/70 border border-slate-800 hover:border-purple-500/40 transition-colors">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Bookings</span>
              <Activity className="w-4 h-4 text-blue-400" />
            </div>
            <div className="text-2xl sm:text-3xl font-black text-white">
              {summary ? summary.totalBookings.toLocaleString('en-IN') : '1,248'}
            </div>
            <div className="flex items-center gap-1 mt-1 text-[11px] text-emerald-400 font-semibold">
              <ArrowUpRight className="w-3.5 h-3.5" />
              <span>+23.4% this month</span>
            </div>
          </div>

          {/* 2. Total Revenue */}
          <div className="p-4 rounded-2xl bg-slate-950/70 border border-slate-800 hover:border-purple-500/40 transition-colors">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Revenue</span>
              <DollarSign className="w-4 h-4 text-emerald-400" />
            </div>
            <div className="text-2xl sm:text-3xl font-black text-white">
              ₹4.8L
            </div>
            <div className="text-[11px] text-purple-300 font-semibold mt-1">
              Commission (20%): ₹96,000
            </div>
          </div>

          {/* 3. AI Alerts */}
          <div
            onClick={() => setSubTab('fraud_detection')}
            className="p-4 rounded-2xl bg-slate-950/70 border border-amber-900/40 hover:border-amber-500/60 transition-colors cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-bold text-amber-400 uppercase tracking-wider">AI Alerts</span>
              <ShieldAlert className="w-4 h-4 text-amber-400 group-hover:scale-110 transition-transform" />
            </div>
            <div className="text-2xl sm:text-3xl font-black text-amber-300">
              08
            </div>
            <div className="text-[11px] text-amber-400/80 font-semibold mt-1">
              {summary?.aiAlertsCount || 8} Active Flags · Review
            </div>
          </div>

          {/* 4. Active Companions */}
          <div className="p-4 rounded-2xl bg-slate-950/70 border border-slate-800 hover:border-purple-500/40 transition-colors">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Active Companions</span>
              <Users className="w-4 h-4 text-purple-400" />
            </div>
            <div className="text-2xl sm:text-3xl font-black text-white">
              {summary ? summary.activeCompanions : '356'}
            </div>
            <div className="text-[11px] text-slate-400 font-semibold mt-1">
              42 Drivers · 128 Guides
            </div>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* SUB-NAVIGATION TABS (The 8 Pillars of the AI Intelligence System)         */}
      {/* ========================================================================= */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none border-b border-slate-800">
        {[
          { id: 'overview', label: 'Executive Briefing', icon: Activity },
          { id: 'business_bi', label: 'Business Intelligence', icon: TrendingUp },
          { id: 'fraud_detection', label: 'Fraud & Anomaly Detection', icon: ShieldAlert, badge: '8 Flags' },
          { id: 'demand_forecast', label: 'Demand Forecasting', icon: Compass },
          { id: 'smart_matching', label: 'Smart Companion Matching', icon: Users },
          { id: 'pricing_advisor', label: 'Pricing Advisor', icon: DollarSign },
          { id: 'support_assistant', label: 'Customer Support Assistant', icon: MessageSquare, badge: 'Disputes' },
          { id: 'doc_prescreen', label: 'Document Pre-Screening', icon: FileCheck },
          { id: 'chat_assistant', label: 'AI Owner Assistant Chat', icon: Bot, badge: 'Gemini' },
          { id: 'audit_logs', label: 'AI Audit & Telemetry', icon: FileText },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = subTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setSubTab(tab.id as any)}
              className={`px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
                isActive
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-950 font-black'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{tab.label}</span>
              {tab.badge && (
                <span
                  className={`px-1.5 py-0.2 rounded-full text-[9px] font-mono font-bold ${
                    isActive ? 'bg-white/20 text-white' : 'bg-slate-800 text-purple-300'
                  }`}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ========================================================================= */}
      {/* 1. SUB-TAB: EXECUTIVE BRIEFING & OVERVIEW                                 */}
      {/* ========================================================================= */}
      {subTab === 'overview' && (
        <div className="space-y-6">
          {/* AI Insights Concept Banner (Exact Prompt Match) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* 1. Demand Forecast Concept Card */}
            <div
              onClick={() => setSubTab('demand_forecast')}
              className="p-5 rounded-2xl bg-slate-900 border border-slate-800 hover:border-purple-500/50 transition-all cursor-pointer group space-y-2"
            >
              <div className="flex items-center gap-2 text-purple-400 font-bold text-xs">
                <Compass className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                <span>Demand Forecast</span>
              </div>
              <h3 className="text-base font-bold text-white">Upcoming Weekend Surge (+2.85x)</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                AI highlights expected booking trends based on historical volume. 
                Strongest deficit in Kolkata Heritage Tours and Goa Driver Chauffeured trips.
              </p>
              <div className="text-[11px] text-purple-400 font-semibold flex items-center gap-1 pt-1">
                <span>View Full Forecast Radar</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </div>
            </div>

            {/* 2. Security Alerts Concept Card */}
            <div
              onClick={() => setSubTab('fraud_detection')}
              className="p-5 rounded-2xl bg-slate-900 border border-amber-900/40 hover:border-amber-500/50 transition-all cursor-pointer group space-y-2"
            >
              <div className="flex items-center gap-2 text-amber-400 font-bold text-xs">
                <ShieldAlert className="w-4 h-4 group-hover:scale-110 transition-transform" />
                <span>Security Alerts</span>
              </div>
              <h3 className="text-base font-bold text-white">8 Unusual Activity Flags</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Review unusual activity flagged by AI before taking manual action. 
                AI assists and explains; final account decisions remain strictly in Admin hands.
              </p>
              <div className="text-[11px] text-amber-400 font-semibold flex items-center gap-1 pt-1">
                <span>Investigate Security Flags</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </div>
            </div>

            {/* 3. Business Recommendations Concept Card */}
            <div
              onClick={() => setSubTab('business_bi')}
              className="p-5 rounded-2xl bg-slate-900 border border-slate-800 hover:border-emerald-500/50 transition-all cursor-pointer group space-y-2"
            >
              <div className="flex items-center gap-2 text-emerald-400 font-bold text-xs">
                <TrendingUp className="w-4 h-4 group-hover:scale-110 transition-transform" />
                <span>Business Recommendations</span>
              </div>
              <h3 className="text-base font-bold text-white">Unlock +₹65K in Driver KYC</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Get suggestions for improving bookings, pricing and service availability. 
                Approving 28 pending driving licences can meet the 32% driver demand spike.
              </p>
              <div className="text-[11px] text-emerald-400 font-semibold flex items-center gap-1 pt-1">
                <span>Explore Growth Recommendations</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </div>
            </div>
          </div>

          {/* Service Category Revenue Share & Top Recommendations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Category Performance Breakdown */}
            <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-white">Revenue by Service Category</h3>
                  <p className="text-xs text-slate-400">Total Volume: ₹4,80,000 across 1,248 Bookings</p>
                </div>
                <span className="px-2.5 py-1 rounded-full bg-purple-950 border border-purple-800 text-purple-300 text-[10px] font-mono font-bold">
                  20% Platform Cut
                </span>
              </div>

              <div className="space-y-3 pt-2">
                {[
                  { name: 'Dining & Social Companionship', share: 42, revenue: '₹2,01,600', bookings: 524, color: 'bg-pink-500', growth: '+14.5%' },
                  { name: 'Tourist Guide & Heritage Walks', share: 28, revenue: '₹1,34,400', bookings: 350, color: 'bg-purple-500', growth: '+26.2%' },
                  { name: 'Driver & Chauffeur Assisted Tours', share: 18, revenue: '₹86,400', bookings: 224, color: 'bg-blue-500', growth: '+32.1%' },
                  { name: 'Social Events & Gatherings', share: 12, revenue: '₹57,600', bookings: 150, color: 'bg-emerald-500', growth: '+8.4%' },
                ].map((cat, idx) => (
                  <div key={idx} className="space-y-1.5 p-3 rounded-2xl bg-slate-950/60 border border-slate-800/80">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-white">{cat.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-purple-300 font-bold">{cat.revenue}</span>
                        <span className="text-[10px] font-bold text-emerald-400">{cat.growth}</span>
                      </div>
                    </div>
                    <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                      <div className={`h-full ${cat.color} rounded-full`} style={{ width: `${cat.share}%` }} />
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-slate-500 font-mono">
                      <span>{cat.bookings} Bookings</span>
                      <span>{cat.share}% Total Market Share</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Actionable Recommendations */}
            <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-white">AI Growth Recommendations</h3>
                  <p className="text-xs text-slate-400">High-priority operational suggestions</p>
                </div>
                <span className="text-xs text-purple-400 font-bold">4 Active</span>
              </div>

              <div className="space-y-3">
                {biData?.recommendations?.map((rec) => (
                  <div
                    key={rec.id}
                    className="p-3.5 rounded-2xl bg-slate-950/80 border border-slate-800 hover:border-slate-700 transition-all space-y-1.5"
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                          rec.priority === 'HIGH'
                            ? 'bg-rose-950 text-rose-300 border border-rose-800'
                            : 'bg-purple-950 text-purple-300 border border-purple-800'
                        }`}
                      >
                        {rec.priority} Priority · {rec.category}
                      </span>
                      <span className="text-[11px] font-bold text-emerald-400">{rec.impact}</span>
                    </div>
                    <h4 className="text-xs font-bold text-white">{rec.title}</h4>
                    <p className="text-[11px] text-slate-400 leading-relaxed">{rec.description}</p>
                    <div className="p-2 rounded-xl bg-purple-950/40 border border-purple-900/40 text-[10px] text-purple-200">
                      <strong>Recommended Action:</strong> {rec.actionableStep}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 2. SUB-TAB: BUSINESS INTELLIGENCE & FINANCIALS                            */}
      {/* ========================================================================= */}
      {subTab === 'business_bi' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-3xl bg-slate-900 border border-slate-800">
            <div>
              <h2 className="text-lg font-bold text-white">Comprehensive Business Intelligence</h2>
              <p className="text-xs text-slate-400">
                Revenue, volume, commission splits, and predictive growth projections.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {(['WEEKLY', 'MONTHLY', 'YEARLY'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTimeframe(t)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    timeframe === t
                      ? 'bg-purple-600 text-white font-black'
                      : 'bg-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Historical vs AI Projected Growth Table */}
          <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-white">Historical Actuals vs AI Predictive Forecast</h3>
                <p className="text-xs text-slate-400">
                  Clearly distinguishing actual audited revenue from future mathematical estimates.
                </p>
              </div>
              <span className="text-xs font-bold text-purple-400 font-mono">Confidence: 91%</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-400 font-mono text-[11px]">
                    <th className="py-2.5 px-3">Timeline Period</th>
                    <th className="py-2.5 px-3">Status</th>
                    <th className="py-2.5 px-3">Revenue (Gross)</th>
                    <th className="py-2.5 px-3">Platform 20%</th>
                    <th className="py-2.5 px-3">Companion 80%</th>
                    <th className="py-2.5 px-3">Bookings</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60 font-mono">
                  {biData?.historicalVsProjected?.map((row, idx) => {
                    const isForecast = !row.actualRevenue && (row.projectedRevenue || 0) > 0;
                    const rev = row.actualRevenue || row.projectedRevenue || 0;
                    const bk = row.actualBookings || row.projectedBookings || 0;
                    return (
                      <tr key={idx} className={isForecast ? 'bg-purple-950/20' : 'hover:bg-slate-800/40'}>
                        <td className="py-3 px-3 font-bold text-white">{row.period}</td>
                        <td className="py-3 px-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${
                              isForecast
                                ? 'bg-purple-950 border border-purple-700 text-purple-300'
                                : 'bg-emerald-950 border border-emerald-700 text-emerald-300'
                            }`}
                          >
                            {isForecast ? 'AI ESTIMATE' : 'AUDITED'}
                          </span>
                        </td>
                        <td className="py-3 px-3 font-bold text-slate-200">₹{rev.toLocaleString('en-IN')}</td>
                        <td className="py-3 px-3 text-purple-300 font-bold">₹{Math.round(rev * 0.2).toLocaleString('en-IN')}</td>
                        <td className="py-3 px-3 text-slate-300">₹{Math.round(rev * 0.8).toLocaleString('en-IN')}</td>
                        <td className="py-3 px-3 text-slate-300">{bk.toLocaleString('en-IN')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 3. SUB-TAB: FRAUD & ANOMALY DETECTION CENTER                              */}
      {/* ========================================================================= */}
      {subTab === 'fraud_detection' && (
        <div className="space-y-6">
          <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-400" />
                <h2 className="text-lg font-bold text-white">AI Fraud & Anomaly Detection Center</h2>
              </div>
              <p className="text-xs text-slate-400 mt-1 max-w-xl">
                Pattern recognition for suspicious booking velocity, card abuse, and location anomalies.
                <strong className="text-amber-300 block mt-0.5">
                  Policy Safeguard: AI alerts require manual Admin verification. Accounts are never blocked automatically.
                </strong>
              </p>
            </div>

            {/* Filter buttons */}
            <div className="flex items-center gap-1.5 bg-slate-950 p-1 rounded-2xl border border-slate-800">
              {(['OPEN', 'HIGH', 'REVIEWED', 'ALL'] as const).map((filter) => (
                <button
                  key={filter}
                  onClick={() => setAlertFilter(filter)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    alertFilter === filter
                      ? 'bg-amber-600 text-white font-black'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          {/* Alerts Feed */}
          <div className="grid grid-cols-1 gap-4">
            {filteredAlerts.map((alert) => (
              <div
                key={alert.id}
                className={`p-5 rounded-3xl border transition-all space-y-3 ${
                  alert.riskLevel === 'HIGH'
                    ? 'bg-slate-900/90 border-rose-900/60 hover:border-rose-700'
                    : alert.riskLevel === 'MEDIUM'
                    ? 'bg-slate-900/90 border-amber-900/60 hover:border-amber-700'
                    : 'bg-slate-900/90 border-slate-800 hover:border-slate-700'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <span
                      className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                        alert.riskLevel === 'HIGH'
                          ? 'bg-rose-950 border border-rose-700 text-rose-300'
                          : alert.riskLevel === 'MEDIUM'
                          ? 'bg-amber-950 border border-amber-700 text-amber-300'
                          : 'bg-blue-950 border border-blue-700 text-blue-300'
                      }`}
                    >
                      {alert.riskLevel} RISK · {alert.anomalyType}
                    </span>
                    <span className="text-xs font-mono text-purple-400 font-bold">ID: {alert.id}</span>
                  </div>

                  <div className="flex items-center gap-3 text-xs text-slate-400 font-mono">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {new Date(alert.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span className="font-bold text-purple-300">Confidence: {alert.confidenceScore}%</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        alert.status === 'OPEN'
                          ? 'bg-amber-950/60 text-amber-300'
                          : alert.status === 'REVIEWED'
                          ? 'bg-blue-950/60 text-blue-300'
                          : 'bg-emerald-950/60 text-emerald-300'
                      }`}
                    >
                      {alert.status}
                    </span>
                  </div>
                </div>

                <div>
                  <h3 className="text-base font-bold text-white">{alert.title}</h3>
                  <p className="text-xs text-slate-300 mt-0.5">{alert.description}</p>
                </div>

                {/* Evidence Box */}
                <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800 space-y-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                    AI Supporting Evidence & Telemetry Logs:
                  </span>
                  <ul className="space-y-1 text-xs text-slate-300">
                    {alert.evidence.map((ev, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-amber-400">•</span>
                        <span>{ev}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Suggested Action & Human Workflow Buttons */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 border-t border-slate-800/80">
                  <div className="text-xs text-purple-300">
                    <strong>AI Recommendation:</strong> {alert.suggestedAction}
                  </div>

                  {alert.status === 'OPEN' && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => handleAlertAction(alert.id, 'DISMISS')}
                        className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition-all cursor-pointer"
                      >
                        Dismiss False Positive
                      </button>
                      <button
                        onClick={() => handleAlertAction(alert.id, 'REVIEW')}
                        className="px-3 py-1.5 rounded-xl bg-purple-700 hover:bg-purple-600 text-white text-xs font-bold transition-all cursor-pointer"
                      >
                        Mark Reviewed
                      </button>
                      <button
                        onClick={() => handleAlertAction(alert.id, 'FLAG_ACCOUNT')}
                        className="px-3 py-1.5 rounded-xl bg-rose-700 hover:bg-rose-600 text-white text-xs font-bold transition-all cursor-pointer"
                      >
                        Flag for Manual Audit
                      </button>
                    </div>
                  )}
                  {alert.adminNotes && (
                    <div className="text-[11px] text-slate-400 italic">
                      Admin Note: {alert.adminNotes}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 4. SUB-TAB: DEMAND FORECASTING & CAPACITY PLANNER                         */}
      {/* ========================================================================= */}
      {subTab === 'demand_forecast' && (
        <div className="space-y-6">
          <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800">
            <div className="flex items-center gap-2">
              <Compass className="w-5 h-5 text-purple-400" />
              <h2 className="text-lg font-bold text-white">AI Demand Forecasting & Capacity Radar</h2>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Historical modeling predicting booking surges by city, service category, and time periods.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {demandData?.cities?.map((c, idx) => (
              <div key={idx} className="p-5 rounded-3xl bg-slate-900 border border-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 font-bold text-white text-base">
                    <MapPin className="w-4 h-4 text-pink-500" />
                    <span>{c.city}</span>
                  </div>
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-[10px] font-black ${
                      c.demandLevel === 'VERY_HIGH'
                        ? 'bg-rose-950 text-rose-300 border border-rose-800'
                        : c.demandLevel === 'HIGH'
                        ? 'bg-purple-950 text-purple-300 border border-purple-800'
                        : 'bg-blue-950 text-blue-300 border border-blue-800'
                    }`}
                  >
                    {c.demandLevel} DEMAND
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                  <div className="p-2.5 rounded-2xl bg-slate-950 border border-slate-800">
                    <span className="text-[10px] text-slate-500 block">Forecast Bookings</span>
                    <span className="text-base font-bold text-white">{c.projectedBookingsNextWeek}</span>
                  </div>
                  <div className="p-2.5 rounded-2xl bg-slate-950 border border-slate-800">
                    <span className="text-[10px] text-slate-500 block">Active Companions</span>
                    <span className="text-base font-bold text-purple-300">{c.currentActiveCompanions}</span>
                  </div>
                </div>

                {c.companionDeficit > 0 && (
                  <div className="p-2 rounded-xl bg-amber-950/40 border border-amber-900/60 text-xs text-amber-300 flex items-center justify-between">
                    <span>Companion Deficit:</span>
                    <strong className="font-mono">+{c.companionDeficit} Needed</strong>
                  </div>
                )}

                <div className="text-[11px] text-slate-400">
                  <span className="text-slate-500">Top Categories:</span> {c.topCategories.join(', ')}
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800/80 text-xs text-slate-400 flex items-start gap-2">
            <Info className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
            <span>{demandData?.limitationsNotice}</span>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 5. SUB-TAB: SMART COMPANION MATCHING SIMULATOR                            */}
      {/* ========================================================================= */}
      {subTab === 'smart_matching' && (
        <div className="space-y-6">
          <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-400" />
              <h2 className="text-lg font-bold text-white">AI Smart Companion Matching Simulator</h2>
            </div>
            <p className="text-xs text-slate-400 mt-1 max-w-xl">
              Simulate and inspect how the platform ranks companions for client preferences 
              using transparent scoring factors (proximity, category expertise, verified rating, response speed).
            </p>
          </div>

          {/* Simulator Form */}
          <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-300 block mb-1">Service Category</label>
              <select
                value={matchingCriteria.category}
                onChange={(e) => setMatchingCriteria({ ...matchingCriteria, category: e.target.value })}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white"
              >
                <option value="Tourist Guide & Heritage Walks">Tourist Guide & Heritage Walks</option>
                <option value="Driver & Chauffeur Assisted Tours">Driver & Chauffeur Assisted Tours</option>
                <option value="Dining & Social Companionship">Dining & Social Companionship</option>
                <option value="Social Events & Parties">Social Events & Parties</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-300 block mb-1">Target City</label>
              <select
                value={matchingCriteria.city}
                onChange={(e) => setMatchingCriteria({ ...matchingCriteria, city: e.target.value })}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white"
              >
                <option value="Kolkata, WB">Kolkata, WB</option>
                <option value="Mumbai, MH">Mumbai, MH</option>
                <option value="Delhi NCR">Delhi NCR</option>
                <option value="Bengaluru, KA">Bengaluru, KA</option>
                <option value="Goa">Goa</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-300 block mb-1">Budget / Tier</label>
              <input
                type="text"
                value={matchingCriteria.budgetTier}
                onChange={(e) => setMatchingCriteria({ ...matchingCriteria, budgetTier: e.target.value })}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={handleRunMatching}
                disabled={isMatchingLoading}
                className="w-full py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold cursor-pointer transition-all flex items-center justify-center gap-2"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>{isMatchingLoading ? 'Analyzing...' : 'Simulate Match'}</span>
              </button>
            </div>
          </div>

          {/* Results Grid */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-white">Recommended Matches & Scoring Breakdown</h3>
            {matchingResults.length === 0 ? (
              <div className="p-8 text-center rounded-3xl bg-slate-900 border border-slate-800 text-xs text-slate-400">
                Click &ldquo;Simulate Match&rdquo; above to run the AI matching algorithm across active companions.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {matchingResults.map((match) => (
                  <div key={match.companionId} className="p-5 rounded-3xl bg-slate-900 border border-slate-800 space-y-3">
                    <div className="flex items-center gap-3">
                      <img src={match.avatarUrl} alt={match.name} className="w-12 h-12 rounded-full object-cover border border-purple-500" />
                      <div>
                        <h4 className="text-base font-bold text-white">{match.name}</h4>
                        <span className="text-xs text-slate-400 font-mono">{match.city} · ₹{match.hourlyRate}/hr</span>
                      </div>
                    </div>

                    <div className="p-3 rounded-2xl bg-purple-950/40 border border-purple-800/40">
                      <div className="flex items-center justify-between text-xs font-bold text-purple-300 mb-1">
                        <span>Compatibility Score:</span>
                        <span className="text-sm text-emerald-400">{match.compatibilityScore}%</span>
                      </div>
                      <div className="grid grid-cols-2 gap-1 text-[10px] text-slate-400 font-mono">
                        <div>Proximity: {match.scoringBreakdown.locationProximity}%</div>
                        <div>Expertise: {match.scoringBreakdown.serviceExpertise}%</div>
                        <div>Trust/Rating: {match.scoringBreakdown.ratingTrust}%</div>
                        <div>Availability: {match.scoringBreakdown.availabilityFit}%</div>
                      </div>
                    </div>

                    <p className="text-xs text-slate-300 leading-relaxed italic">&ldquo;{match.matchReason}&rdquo;</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 6. SUB-TAB: PRICING ADVISOR                                               */}
      {/* ========================================================================= */}
      {subTab === 'pricing_advisor' && (
        <div className="space-y-6">
          <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              <h2 className="text-lg font-bold text-white">AI Smart Pricing Advisor</h2>
            </div>
            <p className="text-xs text-slate-400 mt-1 max-w-xl">
              Market elasticity analysis across Hourly (₹600 floor), 1/3 Day, Half-Day, and Full-Day tiers.
              <strong className="text-purple-300 block mt-0.5">
                Note: Companions set their own rates. Recommendations are non-binding.
              </strong>
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {pricingItems.map((item, idx) => (
              <div key={idx} className="p-5 rounded-3xl bg-slate-900 border border-slate-800 space-y-4">
                <div>
                  <span className="text-[10px] uppercase font-bold tracking-wider text-purple-400 block">{item.city}</span>
                  <h3 className="text-base font-bold text-white">{item.serviceCategory}</h3>
                </div>

                <div className="space-y-2 p-3.5 rounded-2xl bg-slate-950 border border-slate-800 font-mono text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Current Average:</span>
                    <span className="text-white font-bold">₹{item.currentAvgHourly}/hr</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Optimal Hourly Range:</span>
                    <span className="text-emerald-400 font-bold">₹{item.recommendedHourlyRange.min} - ₹{item.recommendedHourlyRange.max}</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-slate-800">
                    <span className="text-slate-400">1/3 Day (6-Hour):</span>
                    <span className="text-purple-300 font-bold">₹{item.package1_3DayRateRecommended}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Half-Day (12-Hour):</span>
                    <span className="text-purple-300 font-bold">₹{item.packageHalfDayRateRecommended}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Full-Day (24-Hour):</span>
                    <span className="text-purple-300 font-bold">₹{item.packageFullDayRateRecommended}</span>
                  </div>
                </div>

                <div className="p-3 rounded-2xl bg-emerald-950/40 border border-emerald-800/40 text-xs text-emerald-300">
                  <strong>Expected Impact:</strong> {item.expectedConversionBoost}
                </div>

                <p className="text-xs text-slate-300 leading-relaxed">{item.justification}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 7. SUB-TAB: CUSTOMER SUPPORT DISPUTE ASSISTANT                            */}
      {/* ========================================================================= */}
      {subTab === 'support_assistant' && (
        <div className="space-y-6">
          <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-400" />
              <h2 className="text-lg font-bold text-white">AI Customer Support & Dispute Assistant</h2>
            </div>
            <p className="text-xs text-slate-400 mt-1 max-w-xl">
              Automatic summarization of client/companion complaints, sentiment assessment, 
              and AI drafted reply recommendations for quick Admin review and dispatch.
            </p>
          </div>

          <div className="space-y-4">
            {disputes.map((disp) => (
              <div key={disp.id} className="p-5 rounded-3xl bg-slate-900 border border-slate-800 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-purple-400">{disp.ticketNumber}</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        disp.priority === 'URGENT'
                          ? 'bg-rose-950 text-rose-300 border border-rose-800'
                          : 'bg-purple-950 text-purple-300 border border-purple-800'
                      }`}
                    >
                      {disp.priority} · {disp.category}
                    </span>
                  </div>
                  <span className="text-xs font-mono text-slate-400">
                    Sentiment: <strong className={disp.sentiment === 'CRITICAL' ? 'text-rose-400' : 'text-slate-300'}>{disp.sentiment}</strong>
                  </span>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-white">Client: {disp.clientName} {disp.companionName && `(Companion: ${disp.companionName})`}</h4>
                  <p className="text-xs text-slate-400 italic mt-0.5">&ldquo;{disp.originalMessage}&rdquo;</p>
                </div>

                {/* AI Drafted Reply */}
                <div className="p-4 rounded-2xl bg-purple-950/30 border border-purple-800/50 space-y-2">
                  <div className="flex items-center justify-between text-xs font-bold text-purple-300">
                    <span className="flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" />
                      AI Suggested Resolution Response:
                    </span>
                    <span className="text-[10px] font-mono text-slate-400">Admin Signoff Required</span>
                  </div>
                  <p className="text-xs text-slate-200 leading-relaxed font-sans">{disp.aiSuggestedReply}</p>
                </div>

                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    onClick={async () => {
                      const res = await draftAISupportReply(disp.id, 'Make it more empathetic and offer 10% coupon code');
                      if (res.success && res.updatedDraft) {
                        setDisputes((prev) =>
                          prev.map((d) => (d.id === disp.id ? { ...d, aiSuggestedReply: res.updatedDraft! } : d))
                        );
                        showNotice('AI Draft updated with coupon offer', 'success');
                      }
                    }}
                    className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold cursor-pointer"
                  >
                    Regenerate Draft with Gemini
                  </button>
                  <button
                    onClick={() => {
                      showNotice(`Reply approved and queued for delivery to ${disp.clientName}`, 'success');
                    }}
                    className="px-4 py-1.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold cursor-pointer flex items-center gap-1.5"
                  >
                    <Check className="w-3.5 h-3.5" />
                    <span>Approve & Dispatch</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 8. SUB-TAB: DOCUMENT PRE-SCREENING ASSISTANT                              */}
      {/* ========================================================================= */}
      {subTab === 'doc_prescreen' && (
        <div className="space-y-6">
          <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800">
            <div className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-purple-400" />
              <h2 className="text-lg font-bold text-white">AI Document Pre-Screening Assistant</h2>
            </div>
            <p className="text-xs text-slate-400 mt-1 max-w-xl">
              Assists the Admin KYC desk by inspecting submitted Driving Licences and IDs for expiry dates, 
              name consistency, and blurriness before human signoff.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Form */}
            <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800 space-y-4">
              <h3 className="text-sm font-bold text-white">Pre-Screen Candidate Document</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-bold text-slate-400 block mb-1">Companion Name</label>
                  <input
                    type="text"
                    value={prescreenInput.companionName}
                    onChange={(e) => setPrescreenInput({ ...prescreenInput, companionName: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-400 block mb-1">Driving Licence Number</label>
                  <input
                    type="text"
                    value={prescreenInput.licenceNumber}
                    onChange={(e) => setPrescreenInput({ ...prescreenInput, licenceNumber: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-400 block mb-1">Licence Expiry Date</label>
                  <input
                    type="date"
                    value={prescreenInput.expiryDate}
                    onChange={(e) => setPrescreenInput({ ...prescreenInput, expiryDate: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white"
                  />
                </div>
                <button
                  onClick={handleRunPrescreen}
                  disabled={isPrescreening}
                  className="w-full py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold cursor-pointer transition-all flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>{isPrescreening ? 'Evaluating Document...' : 'Run AI Document Pre-Screen'}</span>
                </button>
              </div>
            </div>

            {/* Prescreen Results Box */}
            <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800 space-y-4">
              <h3 className="text-sm font-bold text-white">Pre-Screen Findings & Summary</h3>
              {prescreenResult ? (
                <div className="space-y-3 text-xs">
                  <div className="flex items-center justify-between p-3 rounded-2xl bg-slate-950 border border-slate-800">
                    <span className="font-bold text-slate-300">Overall Assessment:</span>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[10px] font-black ${
                        prescreenResult.overallStatus === 'LOW_RISK'
                          ? 'bg-emerald-950 text-emerald-300 border border-emerald-800'
                          : 'bg-rose-950 text-rose-300 border border-rose-800'
                      }`}
                    >
                      {prescreenResult.overallStatus} · {prescreenResult.aiRecommendation}
                    </span>
                  </div>

                  <div className="space-y-2">
                    {prescreenResult.findings.map((f, i) => (
                      <div key={i} className="p-2.5 rounded-xl bg-slate-950/80 border border-slate-800/80 flex items-start justify-between gap-2">
                        <div>
                          <span className="font-bold text-white block">{f.field}: {f.value}</span>
                          <span className="text-[11px] text-slate-400">{f.remark}</span>
                        </div>
                        <span
                          className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                            f.status === 'VALID' ? 'text-emerald-400 bg-emerald-950' : 'text-rose-400 bg-rose-950'
                          }`}
                        >
                          {f.status}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="p-3 rounded-2xl bg-purple-950/40 border border-purple-800/40 text-purple-200">
                    <strong>Admin Summary:</strong> {prescreenResult.summaryText}
                  </div>

                  {onNavigateToKYC && (
                    <button
                      onClick={onNavigateToKYC}
                      className="w-full py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-bold cursor-pointer transition-colors"
                    >
                      Open in Admin Government ID KYC Desk →
                    </button>
                  )}
                </div>
              ) : (
                <div className="p-8 text-center rounded-2xl bg-slate-950 border border-slate-800 text-xs text-slate-400">
                  Run a pre-screen on the left to see instant optical and validity findings.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 9. SUB-TAB: AI OWNER ASSISTANT CHAT (Powered by Google Gemini)            */}
      {/* ========================================================================= */}
      {subTab === 'chat_assistant' && (
        <div className="p-6 rounded-3xl bg-slate-900 border border-purple-900/60 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-800">
            <div className="flex items-center gap-2.5">
              <Bot className="w-6 h-6 text-purple-400" />
              <div>
                <h2 className="text-base font-bold text-white">Private Owner AI Assistant</h2>
                <span className="text-[11px] text-purple-300 font-mono">
                  Grounded with real platform bookings, financials & KYC status
                </span>
              </div>
            </div>
            <span className="px-2.5 py-1 rounded-full bg-emerald-950/60 border border-emerald-800 text-emerald-300 text-[10px] font-mono font-bold flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Gemini 3.8 Flash Online
            </span>
          </div>

          {/* Prompt quick pills */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
            {[
              'How many successful bookings were completed this month?',
              'Which service category generates the most revenue?',
              'What unusual activity or fraud requires review?',
              'What are the current booking trends and recommendations?',
              'বাংলায় প্ল্যাটফর্মের সামারি বলুন',
            ].map((prompt, i) => (
              <button
                key={i}
                onClick={() => handleSendChat(undefined, prompt)}
                className="px-3 py-1.5 rounded-full bg-slate-800/80 hover:bg-purple-900/40 border border-slate-700 hover:border-purple-500 text-[11px] text-slate-300 hover:text-white whitespace-nowrap transition-all cursor-pointer"
              >
                {prompt}
              </button>
            ))}
          </div>

          {/* Messages Scroll Area */}
          <div className="h-96 overflow-y-auto space-y-3.5 p-4 rounded-2xl bg-slate-950 border border-slate-800/80">
            {chatMessages.map((msg) => (
              <div
                key={msg.id}
                className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`}
              >
                <div
                  className={`max-w-2xl p-4 rounded-2xl text-xs leading-relaxed space-y-1.5 ${
                    msg.sender === 'user'
                      ? 'bg-purple-600 text-white font-medium rounded-br-xs'
                      : 'bg-slate-900 border border-purple-900/40 text-slate-200 rounded-bl-xs'
                  }`}
                >
                  <div className="whitespace-pre-line">{msg.text}</div>
                  {msg.sources && msg.sources.length > 0 && (
                    <div className="text-[10px] text-purple-300 font-mono pt-1 border-t border-purple-900/40 flex items-center gap-1">
                      <span>Grounded Data:</span>
                      <span>{msg.sources.join(' • ')}</span>
                    </div>
                  )}
                </div>
                <span className="text-[10px] text-slate-500 font-mono px-1 mt-0.5">{msg.timestamp}</span>
              </div>
            ))}
            {isChatSending && (
              <div className="flex items-center gap-2 text-xs text-purple-400 font-mono italic">
                <Bot className="w-4 h-4 animate-spin" />
                <span>Gemini is analyzing platform facts & compiling briefing...</span>
              </div>
            )}
          </div>

          {/* Chat Input */}
          <form onSubmit={(e) => handleSendChat(e)} className="flex items-center gap-2 pt-2">
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Ask anything about platform revenue, bookings, companions, or safety alerts..."
              className="flex-1 px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
            />
            <button
              type="submit"
              disabled={isChatSending || !chatInput.trim()}
              className="px-5 py-3 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white rounded-2xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shadow-md shadow-purple-950"
            >
              <Send className="w-3.5 h-3.5" />
              <span>Send</span>
            </button>
          </form>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 10. SUB-TAB: AI AUDIT LOGS & TELEMETRY                                    */}
      {/* ========================================================================= */}
      {subTab === 'audit_logs' && (
        <div className="space-y-4">
          <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-white">AI Intelligence Audit Logs & Telemetry</h2>
              <p className="text-xs text-slate-400">
                Traceability and accountability for all model inferences, queries, and admin actions.
              </p>
            </div>
            <span className="font-mono text-xs text-purple-400 font-bold">{auditLogs.length} Log Entries</span>
          </div>

          <div className="overflow-x-auto rounded-3xl bg-slate-900 border border-slate-800 p-2">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-800 text-slate-400 font-mono text-[11px]">
                  <th className="py-2.5 px-3">Timestamp</th>
                  <th className="py-2.5 px-3">Action Type</th>
                  <th className="py-2.5 px-3">Details</th>
                  <th className="py-2.5 px-3">Model</th>
                  <th className="py-2.5 px-3">Latency</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 font-mono text-slate-300">
                {auditLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-800/40">
                    <td className="py-2.5 px-3 text-[11px] text-slate-400">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="py-2.5 px-3 font-bold text-purple-300">{log.actionType}</td>
                    <td className="py-2.5 px-3 text-white max-w-md truncate">{log.details}</td>
                    <td className="py-2.5 px-3 text-slate-400">{log.aiModel}</td>
                    <td className="py-2.5 px-3 text-emerald-400">{log.latencyMs ? `${log.latencyMs}ms` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
